import type { BatteryChemistry, CRateStatus, CRateValidationResult } from '@brt/shared-types'
import { getChemistryPreset } from '../data/chemistryPresets.js'

export interface PeukertResult {
  effectiveCapacityAh: number | null
  correctionFactor: number
  error: string | null
}

export function applyPeukertCorrection(
  ratedCapacityAh: number,
  loadCurrentA: number,
  peukertExponent: number,
  ratedDischargeHours: number,
): PeukertResult {
  if (loadCurrentA <= 0) {
    return {
      effectiveCapacityAh: null,
      correctionFactor: 1,
      error: 'Load current must be greater than 0 for Peukert correction',
    }
  }

  if (ratedCapacityAh <= 0) {
    return {
      effectiveCapacityAh: null,
      correctionFactor: 1,
      error: 'Rated capacity must be greater than 0',
    }
  }

  if (peukertExponent === 1.0) {
    return {
      effectiveCapacityAh: ratedCapacityAh,
      correctionFactor: 1,
      error: null,
    }
  }

  const ratio = ratedCapacityAh / (loadCurrentA * ratedDischargeHours)
  const correctionFactor = Math.pow(ratio, peukertExponent - 1)
  const effectiveCapacityAh = ratedCapacityAh * correctionFactor

  if (!Number.isFinite(effectiveCapacityAh)) {
    return {
      effectiveCapacityAh: null,
      correctionFactor: 1,
      error: 'Peukert correction produced invalid result',
    }
  }

  return {
    effectiveCapacityAh,
    correctionFactor,
    error: null,
  }
}

export function getPeukertExponent(chemistry: BatteryChemistry): number {
  const preset = getChemistryPreset(chemistry)
  return preset.peukertExponent
}

export function validateCRate(
  loadCurrentA: number,
  capacityAh: number,
  chemistry: BatteryChemistry,
): CRateValidationResult {
  const preset = getChemistryPreset(chemistry)

  if (capacityAh <= 0) {
    return {
      cRate: 0,
      maxSafeCRate: preset.maxSafeCRate,
      peakCRate: preset.peakCRate,
      status: 'safe' satisfies CRateStatus,
      recommendation: 'Set a valid battery capacity to evaluate C-rate',
    }
  }

  const cRate = loadCurrentA / capacityAh

  let status: CRateStatus
  let recommendation: string

  if (cRate <= preset.maxSafeCRate) {
    status = 'safe'
    recommendation = `C-rate ${cRate.toFixed(2)}C is within safe limits (${preset.maxSafeCRate}C)`
  } else if (cRate <= preset.peakCRate) {
    status = 'caution'
    recommendation = `C-rate ${cRate.toFixed(2)}C exceeds safe limit (${preset.maxSafeCRate}C) but within peak (${preset.peakCRate}C)`
  } else {
    status = 'danger'
    recommendation = `C-rate ${cRate.toFixed(2)}C exceeds peak limit (${preset.peakCRate}C) — risk of damage`
  }

  return {
    cRate,
    maxSafeCRate: preset.maxSafeCRate,
    peakCRate: preset.peakCRate,
    status,
    recommendation,
  }
}
